import type { CityConfig } from "@/pages/city-template/types";

export const tillamookConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Tillamook County Clerk Vital Records", desc: "Obtain vital records through the Tillamook County Clerk's office.", url: "https://www.co.tillamook.or.us/gov/clerk/" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Tillamook",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "tillamook",
  route: "/cities/tillamook",
  title: "Emergency Passport Tillamook OR | Expedited Passport Tillamook County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Tillamook, OR — as fast as 24 hours. Fast Passport Center serves Tillamook, Tillamook County, Rockaway Beach, Manzanita, Garibaldi, and the north Oregon coast. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Tillamook, Oregon",
  heroSubtitle: "Dairy cooperative business trip to Europe booked? Family beach vacation to Mexico and your passport expired? Canada fishing trip from the Oregon coast? Fast Passport Center helps Tillamook and Tillamook County residents get expedited passports as fast as 24 hours — entirely remotely. The Seattle Passport Agency is 240 miles away, but we serve you without the drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Tillamook%20Oregon%20coastal%20landscape%20with%20the%20iconic%20Tillamook%20Cheese%20Factory%20building%20and%20green%20dairy%20pastures%20surrounded%20by%20the%20coastal%20mountain%20range%2C%20Pacific%20Northwest%20misty%20morning%20atmosphere%20with%20dramatic%20cloud%20formations%20over%20Tillamook%20Bay%2C%20Three%20Capes%20scenic%20route%20ocean%20views%2C%20classic%20Oregon%20coastal%20small%20town%20charm%2C%20photorealistic%20cinematic%20quality%2C%20no%20text%20no%20people&width=1800&height=700&seq=tillamook-or-hero-01&orientation=landscape",
  heroImageAlt: "Tillamook Oregon coastal dairy landscape with Tillamook Bay and coastal mountains",
  geoPosition: "45.4562;-123.8440",
  geoPlacename: "Tillamook, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 240 miles north — approximately 4.5 hours by car via US-101 and I-5",
  },
  nearbyCitiesZips: [
    { name: "Tillamook", zips: "97141" },
    { name: "Rockaway Beach", zips: "97136" },
    { name: "Garibaldi", zips: "97118" },
    { name: "Manzanita", zips: "97130" },
    { name: "Bay City", zips: "97107" },
    { name: "Pacific City", zips: "97135" },
    { name: "Nehalem", zips: "97131" },
    { name: "Wheeler", zips: "97147" },
    { name: "Netarts", zips: "97143" },
    { name: "Oceanside", zips: "97134" },
    { name: "Cloverdale", zips: "97112" },
    { name: "Beaver", zips: "97108" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Tillamook", desc: "Complete your emergency or expedited passport order from anywhere in Tillamook County — no road trip to Seattle or Portland required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Tillamook or Portland", desc: "Ship your documents from The UPS Store in Tillamook or Portland FedEx Office — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for Tillamook County residents far from major passport acceptance centers." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Seattle Passport Agency slots — far faster than calling for an emergency appointment." },
  ],
  testimonials: [
    {
      name: "Karen D.",
      location: "Tillamook, OR",
      rating: 5,
      text: "We had a dairy industry trade trip to the Netherlands booked and I realized my passport was expired three weeks before departure. Fast Passport Center renewed it in 6 days. They walked me through the overnight shipping from The UPS Store in Tillamook. Incredible service for coastal residents far from everything.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Michael R.",
      location: "Rockaway Beach, OR",
      rating: 5,
      text: "Our family beach vacation rental in Puerto Vallarta was booked months ago and my wife's passport was expired. Fast Passport Center got her renewal done in 5 business days from Rockaway Beach. No drive to Portland, no stress. Phenomenal experience.",
      service: "Emergency Passport Processing",
    },
    {
      name: "Susan L.",
      location: "Manzanita, OR",
      rating: 5,
      text: "Needed a child passport for my daughter's school trip to Canada. Found Fast Passport Center with 12 days to go. They gave me the perfect checklist for the Tillamook acceptance step and expedited from there. Daughter made the trip. Absolutely recommend.",
      service: "Child Passport Expedited",
    },
  ],
  facilities: [
    {
      id: "tillamook-post-main",
      name: "Tillamook Post Office",
      type: "USPS Passport Acceptance",
      address: "2200 1st St",
      city: "Tillamook",
      state: "OR",
      zip: "97141",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Tillamook — primary passport acceptance for Tillamook and Tillamook County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Tillamook County residents. Appointment required. Availability can be limited during summer tourist season on the Oregon coast.",
      icon: "ri-mail-line",
    },
    {
      id: "tillamook-county-clerk",
      name: "Tillamook County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "201 Laurel Ave",
      city: "Tillamook",
      state: "OR",
      zip: "97141",
      phone: "(503) 842-3402",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Tillamook — Tillamook County Courthouse",
      website: "https://www.co.tillamook.or.us/gov/clerk/",
      websiteLabel: "Tillamook County Clerk",
      notes: "Tillamook County Clerk in the Courthouse accepts passport applications. Often better appointment availability than the post office, especially in summer.",
      icon: "ri-government-line",
    },
    {
      id: "rockaway-beach-post",
      name: "Rockaway Beach Post Office",
      type: "USPS Passport Acceptance",
      address: "160 S 1st Ave",
      city: "Rockaway Beach",
      state: "OR",
      zip: "97136",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Rockaway Beach — about 15 miles north of Tillamook via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small facility serving north Tillamook County. Very limited appointment availability — Tillamook strongly preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "lincoln-city-post",
      name: "Lincoln City Post Office",
      type: "USPS Passport Acceptance",
      address: "1501 SE Devils Lake Rd",
      city: "Lincoln City",
      state: "OR",
      zip: "97367",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Lincoln City — about 45 miles south via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative acceptance facility for south Tillamook County residents. Lincoln City has better appointment availability than most coastal offices.",
      icon: "ri-mail-line",
    },
    {
      id: "seaside-post",
      name: "Seaside Post Office",
      type: "USPS Passport Acceptance",
      address: "300 Ave A",
      city: "Seaside",
      state: "OR",
      zip: "97138",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Seaside — about 50 miles north via US-101",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative for north Tillamook County residents in Manzanita, Nehalem, and Wheeler areas.",
      icon: "ri-mail-line",
    },
    {
      id: "mcminnville-post",
      name: "McMinnville Post Office",
      type: "USPS Passport Acceptance",
      address: "650 NE 2nd St",
      city: "McMinnville",
      state: "OR",
      zip: "97128",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "McMinnville — about 35 miles east via OR-6 through the coast range",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Inland alternative for Tillamook County residents willing to drive through the coast range. Often significantly better appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Tillamook, Oregon?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Tillamook residents as fast as 24 hours. The Seattle Passport Agency is about 4.5 hours from Tillamook — we serve you entirely remotely via overnight shipping from The UPS Store in Tillamook or Portland FedEx Office. Start online, we handle the rest.",
    },
    {
      q: "How fast can I get an expedited passport from Tillamook, OR?",
      a: "Expedited passport processing from Tillamook takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from The UPS Store in Tillamook or a Portland FedEx Office.",
    },
    {
      q: "Where is the passport acceptance office in Tillamook?",
      a: "Tillamook's primary passport acceptance locations are the Tillamook Post Office (2200 1st St) and the Tillamook County Clerk's Office (201 Laurel Ave), both in downtown Tillamook. McMinnville is a recommended inland alternative with better appointment availability.",
    },
    {
      q: "Do I need a passport for a Canada trip from Tillamook?",
      a: "Yes — all U.S. citizens crossing into Canada require a valid passport. From Tillamook, BC destinations like Vancouver, Victoria, and Whistler are popular. Fast Passport Center expedites renewals in 2–9 business days for Tillamook County BC travelers.",
    },
    {
      q: "How far is Tillamook from the Seattle Passport Agency?",
      a: "Tillamook is approximately 240 miles from the Seattle Passport Agency — about 4.5 hours by car via US-101 and I-5. For life-or-death emergencies requiring same-day processing, a Seattle trip may be necessary. For most urgent travel needs (2–9 business days), Fast Passport Center serves Tillamook entirely remotely.",
    },
    {
      q: "What airport do Tillamook residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international hub for Tillamook travelers — about 75 miles east via OR-6 through the coast range (1.5–2 hours). PDX serves direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
    },
    {
      q: "Does Tillamook's dairy industry generate international passport needs?",
      a: "Yes — the Tillamook County Creamery Association and related dairy cooperatives send employees and executives internationally to dairy industry conferences in Europe (Netherlands, Germany, Switzerland), agricultural trade shows in Asia, and Canadian dairy industry meetings. These trips often require expedited passports.",
    },
    {
      q: "Can I renew my passport from Tillamook without visiting an office?",
      a: "Yes. Most adult Tillamook residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store Tillamook or Portland FedEx, and we handle the Seattle agency submission.",
    },
    {
      q: "Can I get a child passport from Tillamook?",
      a: "Yes. Child passports require an in-person acceptance visit at the Tillamook Post Office or Tillamook County Clerk's Office — both parents must typically be present or provide notarized consent. Fast Passport Center provides the child passport checklist and expedites after the acceptance step.",
    },
    {
      q: "How much does expedited passport processing cost from Tillamook?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Tillamook", address: "2500 N Main Ave, Tillamook, OR 97141", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Tillamook", address: "2111 N Coast Hwy, Tillamook, OR 97141", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM" },
    { name: "Fred Meyer — Tillamook", address: "2500 N Main Ave, Tillamook, OR 97141", hours: "Daily 7 AM – 11 PM" },
    { name: "McMinnville Post Office", address: "650 NE 2nd St, McMinnville, OR 97128", hours: "Mon–Fri 9 AM – 5 PM" },
    { name: "FedEx Office — Portland NW", address: "2208 NW Glisan St, Portland, OR 97210", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Costco Photo Center — Hillsboro", address: "1255 NE 48th Ave, Hillsboro, OR 97124", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "Walgreens — McMinnville", address: "1550 N Baker St, McMinnville, OR 97128", hours: "Daily 8 AM – 10 PM" },
    { name: "Target Photo — Tanasbourne", address: "18077 NW Evergreen Pkwy, Beaverton, OR 97006", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Tillamook Driver & Motor Vehicle Services",
      address: "319A Main Ave, Tillamook, OR 97141",
      hours: "Mon–Fri 8:30 AM – 5:00 PM (closed noon–1pm)",
      note: "Tillamook DMV — driver licenses, REAL ID, and state ID cards for Tillamook County residents.",
      featured: true,
    },
    {
      name: "McMinnville Driver & Motor Vehicle Services",
      address: "435 NE Evans St, McMinnville, OR 97128",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "McMinnville DMV — alternative for Tillamook County residents willing to drive through the coast range.",
      featured: false,
    },
    {
      name: "Astoria Driver & Motor Vehicle Services",
      address: "1745 Marine Dr, Astoria, OR 97103",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Astoria DMV — serves north Tillamook County residents in the Manzanita and Nehalem areas.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international hub — about 75 miles east via OR-6 (1.5–2 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International Airport is the primary international departure hub for Tillamook travelers. PDX offers direct flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City — a 1.5–2 hour drive from Tillamook.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "About 240 miles north via US-101 and I-5 (4.5 hours)",
      address: "17801 International Blvd, Seattle, WA 98158",
      note: "SEA-TAC is an alternative major international hub with more flight options, especially for Asia, Alaska, and European routes.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA-TAC Airport Website",
      featured: false,
    },
    {
      code: "EUG",
      name: "Eugene Airport",
      distance: "About 175 miles south via US-101 and OR-126 (3.5 hours)",
      address: "28801 Douglas Dr, Eugene, OR 97402",
      note: "Regional airport with limited domestic connections. Portland International is strongly preferred for all Tillamook international travel.",
      flightUrl: "https://www.flyeug.com/",
      flightLabel: "EUG Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Tillamook & Tillamook County",
      description: "Dairy industry trip to Europe imminent? Mexico beach vacation departure approaching? Need a passport fast from the Oregon coast? Fast Passport Center handles emergency passport processing for Tillamook and Tillamook County residents — as fast as 24 hours. Serve entirely remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired and you live on the coast far from passport agencies? Tillamook residents renew fast through Fast Passport Center — ship from The UPS Store Tillamook or Portland FedEx overnight, we handle the expedited submission.",
      bullets: ["Fastest renewal for Tillamook County residents", "Ship overnight from Tillamook or Portland", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Tillamook or before a coast-to-Europe trip? We guide Tillamook County travelers through DS-64 and expedited replacement — no Seattle trip required.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Tillamook Families",
      description: "Tillamook families heading to Canada, Mexico, or European vacations need child passports. We guide you through the Tillamook Post Office or County Clerk acceptance step.",
      bullets: ["Both parents typically required to consent", "Tillamook Post Office or County Clerk guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Tillamook? In-person acceptance at the Tillamook Post Office or County Clerk is required for first-timers. We guide you through every step and expedite from there.",
      bullets: ["Step-by-step acceptance guidance for Tillamook facilities", "Photo location guide for Tillamook and coastal area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for dairy industry professionals, frequent travelers, and Tillamook County residents with complex passport needs.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for dairy industry travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Tillamook, Oregon residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Tillamook and Tillamook County residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Tillamook, Oregon — ship overnight from The UPS Store Tillamook or Portland FedEx." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Tillamook travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Tillamook and Tillamook County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Tillamook, Oregon and the north Oregon coast." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Mexico Beach Vacations Without Checking Passport Validity", description: "Tillamook County residents love escaping the coastal rain for Mexican beaches (Puerto Vallarta, Cabo, Cancun) in winter. Many book the trip months ahead but forget to check passport expiration until packing begins — often 2–3 weeks before departure." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from drugstores or self-taken phone photos frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks when you're already far from passport agencies. Our pre-submission review catches all errors." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Underestimating Tillamook's Remote Location from Passport Agencies", description: "Tillamook is 240 miles from the Seattle Passport Agency and 75 miles from Portland — the coast range drive on OR-6 adds time and weather risk. Use Fast Passport Center's overnight shipping service to avoid last-minute stressful drives." },
    { icon: "ri-calendar-close-line", title: "Dairy Industry Travel — Last-Minute Passport Discovery", description: "Tillamook dairy industry professionals attending European trade shows or Canadian dairy conferences often discover passport issues at the last minute. Start passport checks 6 months before any international industry trip." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Tillamook travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Tillamook County acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in Tillamook or Portland FedEx Office for overnight delivery. Both are reliable options for Tillamook County residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — bypassing the public appointment backlog that stretches for weeks." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Tillamook or Tillamook County address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 4.5 hours from Tillamook",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Long wait times even with appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Tillamook or Portland",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; can be longer in summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, Seattle only", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to Seattle — 4.5 hours from Tillamook", fpc: "Ship overnight from Tillamook or Portland" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Oregon Coast Accessibility", agency: "Not designed for remote coastal residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 9-hour Seattle round trip cost", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Seaside", route: "/cities/seaside" },
    { name: "Emergency passport help in Lincoln City", route: "/cities/lincoln-city" },
    { name: "Fast passport renewal in Portland", route: "/cities/portland" },
    { name: "Rush passport services for Oregon residents", route: "/states/oregon" },
    { name: "Expedited passports statewide in Oregon", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in Tillamook or a Portland FedEx Office — both available weekday evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Tillamook and the entire Tillamook County coast remotely",
  serviceAreaIntro: "Whether you are in Tillamook, Rockaway Beach, Garibaldi, Manzanita, Pacific City, or anywhere in Tillamook County — Fast Passport Center makes emergency and expedited passport processing simple without a drive to Seattle.",
  nearbyCitiesIntro: "We serve all Tillamook County zip codes and surrounding communities including Rockaway Beach, Garibaldi, Manzanita, Bay City, Pacific City, Nehalem, Wheeler, Netarts, and Oceanside.",
  howItWorksIntro: "Getting an emergency or expedited passport from Tillamook is simpler than a 4.5-hour drive to Seattle. Most Tillamook County clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Tillamook and Tillamook County passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Tillamook, Rockaway Beach, and north Oregon coast travelers who needed their passports fast.",
  faqIntro: "The most common questions Tillamook and Tillamook County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Tillamook and Tillamook County Residents",
    intro: "Tillamook is Oregon's dairy capital — home to the Tillamook County Creamery Association and surrounded by green coastal pastures, the Tillamook State Forest, and the Three Capes Scenic Route. Its remote coastal location and unique dairy industry identity create very specific international travel patterns for Tillamook County residents.",
    cards: [
      {
        icon: "ri-store-2-line",
        title: "Dairy Industry International Travel — the Tillamook Factor",
        subtitle: "The Tillamook County Creamery Association drives unique passport needs",
        description: "Tillamook's dairy industry generates significant international travel — to the Netherlands (dairy technology and trade), Germany (processing equipment), Switzerland (cheese competitions), and Canada (agricultural cooperative meetings). Tillamook dairy professionals travel to European dairy expos, Asian agricultural trade shows (Japan, South Korea), and Canadian dairy conferences. These trips are often scheduled with 4–8 weeks notice — exactly the window for expedited passport processing from Tillamook County.",
        tip: "Dairy industry professionals in Tillamook should maintain a passport with at least 12 months of validity at all times. European dairy expos often have short lead times — 2–9 business day expedited processing handles these timelines perfectly.",
      },
      {
        icon: "ri-sun-line",
        title: "Winter Mexico & Costa Rica Beach Escapes",
        subtitle: "Tillamook County's annual migration to the sun",
        description: "Tillamook County residents get 90+ inches of rain annually — creating a powerful motivation for winter beach escapes to Mexico and Costa Rica. Puerto Vallarta, Cabo San Lucas, Riviera Maya, and Guanacaste Costa Rica are the top destinations. These trips are typically booked in October–November for January–March travel. Passport issues are commonly discovered 2–4 weeks before departure — the peak season for emergency and expedited passport requests from Tillamook.",
        tip: "Check passport expiration dates in September before booking that Mexico winter escape. The 2–4 weeks between booking and departure is perfect for Fast Passport Center expedited processing (2–9 business days).",
      },
      {
        icon: "ri-map-pin-2-line",
        title: "British Columbia and Pacific Northwest Canada Travel",
        subtitle: "BC is Tillamook's most accessible international destination",
        description: "British Columbia is popular with Tillamook County residents for quick international getaways — Vancouver, Victoria, Tofino, and Whistler. Passport requirements for Canadian entry are strict for air travel, and many Tillamook residents are surprised to discover they need a passport for what feels like a local trip. BC summer trips are planned in March–May, and winter ski trips in October–November.",
        tip: "Canada requires a valid passport for air entry. Check your passport's expiration before booking any BC trip — the 2–9 business day expedited window handles most BC travel timelines.",
      },
      {
        icon: "ri-ship-2-line",
        title: "Alaska Cruises from Seattle — Tillamook's Ocean Connection",
        subtitle: "Alaska Inside Passage cruises from Seattle (4.5 hours north)",
        description: "Alaska Inside Passage cruises from Seattle are popular with Tillamook County residents. These cruises are typically booked 6–12 months in advance, but passport checks happen at the last minute — often when printing documents or at final payment. Fast Passport Center handles expedited passports for Tillamook cruise travelers in 2–9 business days.",
        tip: "Alaska cruise bookings often happen in September for the following summer. Check passport expiration the same day you book. If your passport expires within 6 months of the cruise return date, renew immediately.",
      },
    ],
    callout: {
      icon: "ri-store-2-line",
      title: "Tillamook, Oregon — Where Dairy Meets the Pacific",
      description: "Tillamook sits at the intersection of dairy agriculture, Pacific Ocean access, and Oregon coast community. The city's unique identity — home to the Tillamook County Creamery Association, surrounded by dairy pastures meeting the ocean, and deeply connected to the coast — creates a distinctive set of international travel patterns. Fast Passport Center has served Tillamook County residents with expedited passports for dairy industry travel, winter beach escapes, BC adventures, and Alaska cruise departures.",
    },
  },
  destinations: {
    title: "Where Tillamook Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Tillamook and north Oregon coast clients. Dairy industry travel, Mexico winter escapes, BC proximity, and Alaska cruise culture create unique passport urgency situations for Tillamook County residents.",
    stats: [
      { stat: "240 mi", label: "to Seattle Passport Agency — the closest regional passport office from Tillamook" },
      { stat: "75 mi", label: "to Portland International Airport (PDX) — primary international hub via OR-6" },
      { stat: "5 hrs", label: "to Vancouver BC — Tillamook's most popular Canadian destination" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Tillamook" },
    ],
    destinations: [
      { name: "Mexico — Puerto Vallarta, Cabo, Riviera Maya", context: "The #1 escape from Tillamook's coastal rain — winter beach trips booked in October for January departure", urgency: "October–November Mexico booking; passport expiration discovered when gathering documents for winter trip — 2–4 weeks before departure", icon: "ri-sun-cloudy-line" },
      { name: "Netherlands & Western Europe — Dairy Trade", context: "Tillamook dairy professionals attending European dairy expos and equipment trade shows in the Netherlands and Germany", urgency: "Trade show dates are fixed; invitations arrive 4–8 weeks before — expedited processing handles this window perfectly", icon: "ri-store-2-line" },
      { name: "British Columbia — Vancouver, Victoria, Whistler", context: "Tillamook's closest international destination — family trips to Vancouver, romantic weekends in Victoria, ski trips to Whistler", urgency: "BC ski season booked in November; BC summer trips booked in March — passport discovery happens at booking time", icon: "ri-map-pin-2-line" },
      { name: "Alaska — Inside Passage Cruises", context: "Alaska cruises from Seattle — popular with Tillamook County residents who book 6–12 months ahead", urgency: "Cruise bookings discover passport issues at final payment or at document printing — 90–30 days before departure", icon: "ri-ship-2-line" },
      { name: "Costa Rica — Guanacaste & Arenal", context: "Eco-adventure destination popular with Tillamook's outdoor-oriented community for winter escapes", urgency: "November–January booking for February–April travel; passport renewal needed with 2–6 weeks before departure", icon: "ri-leaf-line" },
      { name: "Japan & Pacific Rim", context: "Agricultural trade (Japanese dairy technology exchange) and cultural travel from Tillamook County", urgency: "Agricultural trade missions planned 8–12 weeks out; expedited processing serves these timelines comfortably", icon: "ri-ship-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Oregon coast and northwest Oregon with the same expedited passport expertise.",
    crossLinks: [
      { name: "Seaside", route: "/cities/seaside" },
      { name: "Lincoln City", route: "/cities/lincoln-city" },
      { name: "Portland", route: "/cities/portland" },
    ],
  },
};